import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import LocationModal from "../../LocationModal/LocationModal";
import NavbarDesktop from "../../Navbar/NavbarDesktop";
import LocationDisplay from "../components/LocationDisplay/LocationDisplay";
import { trackEvent } from "../../../hooks/useGTM";
import { trackEvent as trackClarityEvent } from "../../../hooks/useClarity";
import { useOptimisticCartQuantity } from "../../../hooks/useOptimisticCartQuantity";

import styles from "./DesktopHeader.module.css";
// import Topbar from "../components/Topbar/Topbar";

const DesktopHeader = ({ toggleMiniCart }: { toggleMiniCart: () => void }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Get location from Redux store
  const location = useSelector((state: any) => state.location);

  // Use optimistic cart quantity for real-time sync across components
  const cartQuantity = useOptimisticCartQuantity();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Track search event in GTM
      trackEvent("product_search", {
        search_string: searchQuery.trim(),
        search_type: "desktop_header",
        device_type: "desktop",
      });

      // Track search event in Clarity
      trackClarityEvent("product_search", {
        search_string: searchQuery.trim(),
        search_type: "desktop_header",
        device_type: "desktop",
      });

      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className={styles.header}>
      {/* <Topbar /> */}
      <div className={styles.headerContainer}>
        <div className={styles.logoContainer}>
          <a
            href="/"
            title="Ir a la página principal de Evefant"
            aria-label="Página principal de Evefant"
          >
            <img
              src={`${process.env.REACT_APP_IMAGE_FOLDER}/logo_evefant_azul_24f13dfd09.png`}
              alt="Logo completo de Evefant"
              className={styles.logo}
              title="Evefant - Tu tienda de celebraciones"
              width={135}
              height={32}
            />
          </a>
        </div>

        <div
          className={styles.locationContainer}
          onClick={() => setModalOpen(true)}
          role="button"
          tabIndex={0}
          aria-label={`Averigua cuánto nos tardaremos, ¿Dónde recibirás tu pedido? Ubicación actual: ${
            location?.name || "No seleccionada"
          }`}
          title="Cambiar ubicación de entrega"
          onKeyDown={(e) => e.key === "Enter" && setModalOpen(true)}
        >
          <LocationDisplay location={location} variant="desktop" />
        </div>
        {modalOpen && (
          <LocationModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}

        <div className={styles.searchContainer}>
          <form
            onSubmit={handleSearch}
            className={styles.searchForm}
            role="search"
            aria-label="Buscador de productos"
          >
            <SearchIcon className={styles.searchIcon} aria-hidden="true" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Busca globos, chocolates, tortas ..."
              className={styles.searchInput}
              aria-label="Buscar productos en Evefant"
              title="Buscar productos en Evefant"
            />
          </form>
        </div>

        <div className={styles.iconsContainer}>
          <div
            className={styles.profileIcon}
            role="button"
            tabIndex={0}
            aria-label="Acceder a mi cuenta de usuario"
            title="Acceder a mi cuenta"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                window.location.href = "/account";
              }
            }}
            onClick={() => {
              window.location.href = "/account";
            }}
          >
            <AccountCircleOutlinedIcon aria-hidden="true" />
          </div>
          <div
            className={styles.cartIcon}
            onClick={toggleMiniCart}
            role="button"
            tabIndex={0}
            aria-label={`Carrito de compras, ${cartQuantity} ${
              cartQuantity === 1 ? "artículo" : "artículos"
            }`}
            title="Ver carrito de compras"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                toggleMiniCart();
              }
            }}
          >
            <ShoppingCartOutlinedIcon aria-hidden="true" />
            {cartQuantity > 0 && (
              <div className={styles.cartBadge} aria-hidden="true">
                {cartQuantity}
              </div>
            )}
          </div>
        </div>
      </div>
      <NavbarDesktop />
    </div>
  );
};

export default DesktopHeader;
